import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Entity } from "models/entity";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { SKSpinner } from "components/customElements/skSpinner";
import EmptyState from "components/EmptyState";
import { useApi } from "hooks/useApi";
import { usePriceQuote } from "hooks/usePriceQuote";
import { baseFetcher } from "utils/baseFetcher";

import { AVAILABLE_TIMESPAN } from "../PriceTarget/utils";
import { chartPriceUrl, consensusTargetUrl, isDataEmpty } from "../utils";

interface ConsensusPriceTargetProps {
  entity: Entity;
}

const ConsensusPriceTarget = ({ entity }: ConsensusPriceTargetProps) => {
  const { data: priceData } = useApi(
    chartPriceUrl(
      entity.id,
      AVAILABLE_TIMESPAN.find((t) => t.id === "1Y")?.since!
    ),
    baseFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data: consensusTarget, isValidating: isValidatingConsensusTarget } =
    useApi(consensusTargetUrl(entity.id), baseFetcher, {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    });

  const {
    fest_estpt,
    fest_estpt_low,
    fest_estpt_high,
    fest_estpt_median,
    fest_estpt_stddev,

    p_l,
  } = consensusTarget || {};

  const priceQuote = usePriceQuote(entity.security);
  const { price = p_l?.value, pctChangeYTD = "0" } = priceQuote;

  const currentPrice = parseFloat(price);
  const twelveMonthAvgTarget = fest_estpt?.value;
  const price1YearAgo = priceData?.graph?.open?.[0];
  const returnPotential = (twelveMonthAvgTarget - currentPrice) / currentPrice;
  const trailing1YReturn = (currentPrice - price1YearAgo) / price1YearAgo;

  if (
    !isValidatingConsensusTarget &&
    isDataEmpty(consensusTarget, [
      "fest_estpt",
      "fest_estpt_low",
      "fest_estpt_high",
      "fest_estpt_median",
      "fest_estpt_stddev",
    ])
  ) {
    return (
      <CustomCard className="flex-1 p-[15px]">
        <div
          className={twMerge(
            "flex-1",
            "font-medium",
            "text-[16px]",
            "text-[#1f1f20]"
          )}
        >
          Consensus Price Target
        </div>
        <EmptyState
          type="data"
          className="pt-[50px] md:pt-[50px] pb-[50px] md:pb-[50px]"
        />
      </CustomCard>
    );
  }

  return (
    <CustomCard className="flex-1 p-[15px] shadow-[0_2px_15px_rgba(0,0,0,0.15)]">
      <div className="flex space-x-[15px] mb-[30px]">
        <div
          className={twMerge(
            "flex-1",
            "font-medium",
            "text-[16px]",
            "text-[#1f1f20]"
          )}
        >
          Consensus Price Target
        </div>
        <div className="font-bold">
          {numeral(fest_estpt?.value).format("0,0.[00]")}
        </div>
      </div>
      {isValidatingConsensusTarget ? (
        <div className="w-full flex justify-center">
          <SKSpinner />
        </div>
      ) : (
        <div className="space-y-[30px]">
          <OverlayTrigger
            overlay={
              <Tooltip>
                Median: {numeral(fest_estpt_median?.value).format("0,0.00")}
                <br />
                Std Dev: {numeral(fest_estpt_stddev?.value).format("0,0.00")}
              </Tooltip>
            }
          >
            <div className="flex space-x-[15px]">
              <div className="flex-0.5 text-[13px]">
                {numeral(fest_estpt_low?.value).format("0,0.00")}
              </div>
              <div className="flex-1 flex">
                <div
                  className="h-[1px] border-0 border-b border-solid border-bearish-red mt-[10px]"
                  style={{
                    width: `${
                      ((fest_estpt_median?.value - fest_estpt_low?.value) /
                        (fest_estpt_high?.value - fest_estpt_low?.value)) *
                      100
                    }%`,
                  }}
                >
                  <div
                    className={twMerge(
                      "h-[22px] bg-bearish-red opacity-50 float-right mt-[-10px]",
                      "border-0 border-r border-solid border-active-black"
                    )}
                    style={{
                      width: `${
                        (fest_estpt_stddev?.value /
                          (fest_estpt_median?.value - fest_estpt_low?.value)) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>
                <div
                  className="h-[1px] border-0 border-b border-solid border-bullish-green mt-[10px]"
                  style={{
                    width: `${
                      ((fest_estpt_high?.value - fest_estpt_median?.value) /
                        (fest_estpt_high?.value - fest_estpt_low?.value)) *
                      100
                    }%`,
                  }}
                >
                  <div
                    className="h-[22px] bg-bullish-green opacity-50 float-left mt-[-10px]"
                    style={{
                      width: `${
                        (fest_estpt_stddev?.value /
                          (fest_estpt_high?.value - fest_estpt_median?.value)) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
              <div className="flex-0.5 text-[13px]">
                {numeral(fest_estpt_high?.value).format("0,0.00")}
              </div>
            </div>
          </OverlayTrigger>
          <div className="space-y-[9px] text-[12px]">
            <div className="flex pb-[9px] border-0 border-b border-solid border-inactive-gray">
              <div className="flex-1">Current Price</div>
              <div className="font-bold">
                {numeral(currentPrice).format("0,0.[00]")}
              </div>
            </div>
            <div className="flex pb-[9px] border-0 border-b border-solid border-inactive-gray">
              <div className="flex-1">Return Potential</div>
              <div
                className={twMerge(
                  "font-bold",
                  returnPotential > 0
                    ? "text-bullish-green"
                    : "text-bearish-red"
                )}
              >
                {numeral(returnPotential).format("0,0.00%")}
              </div>
            </div>
            <div className="flex pb-[9px]">
              <div className="flex-1">Trailing 1Y Price Return</div>
              <div
                className={twMerge(
                  "font-bold",
                  trailing1YReturn > 0
                    ? "text-bullish-green"
                    : "text-bearish-red"
                )}
              >
                {numeral(trailing1YReturn).format("0,0.00%")}
              </div>
            </div>
          </div>
        </div>
      )}
    </CustomCard>
  );
};

export default ConsensusPriceTarget;
